import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  FireIcon,
  AcademicCapIcon,
  PlusIcon,
  TrashIcon,
  UserIcon,
  HeartIcon
} from '@heroicons/react/24/outline';

export default function WorkoutDietPage() {
  const [members, setMembers] = useState([]);
  const [workoutPlans, setWorkoutPlans] = useState([]);
  const [dietPlans, setDietPlans] = useState([]);
  const [activeTab, setActiveTab] = useState('workout'); // 'workout' | 'diet'
  const [loading, setLoading] = useState(true);

  // Form states
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [planName, setPlanName] = useState('');
  const [notes, setNotes] = useState('');

  // Workout state
  const [dayName, setDayName] = useState('Monday (Chest & Triceps)');
  const [exerciseName, setExerciseName] = useState('Bench Press');
  const [setsReps, setSetsReps] = useState('4 Sets x 12 Reps');
  const [scheduleItems, setScheduleItems] = useState([]);

  // Diet state
  const [targetCalories, setTargetCalories] = useState(2200);
  const [protein, setProtein] = useState(160);
  const [carbs, setCarbs] = useState(210);
  const [fats, setFats] = useState(65);
  const [mealName, setMealName] = useState('Breakfast');
  const [mealItems, setMealItems] = useState('4 Egg Whites, 1 Oats Bowl, 1 Banana');
  const [mealsList, setMealsList] = useState([]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [mRes, wRes, dRes] = await Promise.all([
        api.get('/api/members'),
        api.get('/api/workout-plans'),
        api.get('/api/diet-plans')
      ]);
      setMembers(mRes.data.members || []);
      setWorkoutPlans(wRes.data.plans || []);
      setDietPlans(dRes.data.plans || []);
      if (mRes.data.members?.length > 0) {
        setSelectedMemberId(mRes.data.members[0].id);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddScheduleItem = () => {
    if (!exerciseName.trim()) return;
    setScheduleItems([...scheduleItems, { day: dayName, exercise: exerciseName, sets_reps: setsReps }]);
    setExerciseName('');
  };

  const handleAddMealItem = () => {
    if (!mealItems.trim()) return;
    setMealsList([...mealsList, { meal_type: mealName, items: mealItems }]);
    setMealItems('');
  };

  const handleSaveWorkoutPlan = async (e) => {
    e.preventDefault();
    if (!selectedMemberId || !planName.trim()) {
      alert('Please select a member and enter a plan name.');
      return;
    }
    try {
      await api.post('/api/workout-plans', {
        member_id: selectedMemberId,
        plan_name: planName,
        schedule: scheduleItems,
        notes
      });
      alert('Workout Plan saved successfully!');
      setPlanName('');
      setScheduleItems([]);
      fetchInitialData();
    } catch (err) {
      alert('Failed to save workout plan');
    }
  };

  const handleSaveDietPlan = async (e) => {
    e.preventDefault();
    if (!selectedMemberId || !planName.trim()) {
      alert('Please select a member and enter a plan name.');
      return;
    }
    try {
      await api.post('/api/diet-plans', {
        member_id: selectedMemberId,
        plan_name: planName,
        target_calories: targetCalories,
        protein_grams: protein,
        carbs_grams: carbs,
        fats_grams: fats,
        meals: mealsList,
        notes
      });
      alert('Diet Plan saved successfully!');
      setPlanName('');
      setMealsList([]);
      fetchInitialData();
    } catch (err) {
      alert('Failed to save diet plan');
    }
  };

  const handleDeleteWorkout = async (id) => {
    if (!window.confirm('Delete this workout plan?')) return;
    await api.delete(`/api/workout-plans/${id}`);
    fetchInitialData();
  };

  const handleDeleteDiet = async (id) => {
    if (!window.confirm('Delete this diet plan?')) return;
    await api.delete(`/api/diet-plans/${id}`);
    fetchInitialData();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-black text-gray-900 flex items-center gap-2">
          <FireIcon className="w-8 h-8 text-orange-600" />
          Member Workout & Diet Planner
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Assign personalized workout routines and nutrition meal plans to your gym members.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-3 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('workout')}
          className={`pb-3 font-bold text-sm border-b-2 transition-all flex items-center gap-2 ${
            activeTab === 'workout'
              ? 'border-orange-600 text-orange-600'
              : 'border-transparent text-gray-500 hover:text-gray-900'
          }`}
        >
          <AcademicCapIcon className="w-5 h-5" /> Workout Routines
        </button>
        <button
          onClick={() => setActiveTab('diet')}
          className={`pb-3 font-bold text-sm border-b-2 transition-all flex items-center gap-2 ${
            activeTab === 'diet'
              ? 'border-green-600 text-green-600'
              : 'border-transparent text-gray-500 hover:text-gray-900'
          }`}
        >
          <HeartIcon className="w-5 h-5" /> Nutrition & Diet Plans
        </button>
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Plan Creator Form */}
        <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm space-y-4">
          <h2 className="text-lg font-bold text-gray-900">
            Create {activeTab === 'workout' ? 'Workout Routine' : 'Diet & Nutrition Plan'}
          </h2>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Select Member</label>
            <select
              value={selectedMemberId}
              onChange={(e) => setSelectedMemberId(e.target.value)}
              className="w-full text-sm border border-gray-300 p-2.5 rounded-xl"
            >
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.first_name} {m.last_name} ({m.phone})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Plan Title</label>
            <input
              type="text"
              placeholder={activeTab === 'workout' ? 'e.g. 5-Day Muscle Hypertrophy Split' : 'e.g. High Protein Lean Mass Diet'}
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              className="w-full text-sm border border-gray-300 p-2.5 rounded-xl"
            />
          </div>

          {activeTab === 'workout' ? (
            <div className="space-y-3 bg-gray-50 p-4 rounded-xl border border-gray-200">
              <h3 className="text-xs font-bold text-gray-700 uppercase">Add Daily Exercises</h3>
              <input
                type="text"
                placeholder="Day / Muscles (e.g. Monday - Chest & Triceps)"
                value={dayName}
                onChange={(e) => setDayName(e.target.value)}
                className="w-full text-sm border border-gray-300 p-2 rounded-lg"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  placeholder="Exercise Name (e.g. Incline Bench Press)"
                  value={exerciseName}
                  onChange={(e) => setExerciseName(e.target.value)}
                  className="w-full text-sm border border-gray-300 p-2 rounded-lg"
                />
                <input
                  type="text"
                  placeholder="Sets & Reps (e.g. 4 Sets x 10 Reps)"
                  value={setsReps}
                  onChange={(e) => setSetsReps(e.target.value)}
                  className="w-full text-sm border border-gray-300 p-2 rounded-lg"
                />
              </div>
              <button
                type="button"
                onClick={handleAddScheduleItem}
                className="w-full bg-orange-100 text-orange-800 text-xs font-bold py-2 rounded-lg hover:bg-orange-200"
              >
                + Add Exercise to Schedule
              </button>

              {scheduleItems.length > 0 && (
                <div className="space-y-1.5 mt-2">
                  {scheduleItems.map((item, idx) => (
                    <div key={idx} className="text-xs bg-white p-2 rounded border flex justify-between">
                      <span><strong>{item.day}:</strong> {item.exercise} ({item.sets_reps})</span>
                      <button onClick={() => setScheduleItems(scheduleItems.filter((_, i) => i !== idx))} className="text-red-600 font-bold">×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3 bg-gray-50 p-4 rounded-xl border border-gray-200">
              <h3 className="text-xs font-bold text-gray-700 uppercase">Macros Target & Meals</h3>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div>
                  <label className="text-[10px] font-bold text-gray-500">Calories</label>
                  <input type="number" value={targetCalories} onChange={(e) => setTargetCalories(e.target.value)} className="w-full text-xs p-1.5 border rounded text-center" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-500">Protein (g)</label>
                  <input type="number" value={protein} onChange={(e) => setProtein(e.target.value)} className="w-full text-xs p-1.5 border rounded text-center" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-500">Carbs (g)</label>
                  <input type="number" value={carbs} onChange={(e) => setCarbs(e.target.value)} className="w-full text-xs p-1.5 border rounded text-center" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-500">Fats (g)</label>
                  <input type="number" value={fats} onChange={(e) => setFats(e.target.value)} className="w-full text-xs p-1.5 border rounded text-center" />
                </div>
              </div>

              <div className="space-y-2 mt-2">
                <select value={mealName} onChange={(e) => setMealName(e.target.value)} className="w-full text-xs p-2 border rounded-lg">
                  <option value="Breakfast">Breakfast</option>
                  <option value="Lunch">Lunch</option>
                  <option value="Pre-Workout Snack">Pre-Workout Snack</option>
                  <option value="Post-Workout Shake">Post-Workout Shake</option>
                  <option value="Dinner">Dinner</option>
                </select>
                <input
                  type="text"
                  placeholder="Meal items (e.g. 5 Egg whites, 1 bowl oats, 10 almonds)"
                  value={mealItems}
                  onChange={(e) => setMealItems(e.target.value)}
                  className="w-full text-sm border p-2 rounded-lg"
                />
                <button
                  type="button"
                  onClick={handleAddMealItem}
                  className="w-full bg-green-100 text-green-800 text-xs font-bold py-2 rounded-lg hover:bg-green-200"
                >
                  + Add Meal to Plan
                </button>

                {mealsList.length > 0 && (
                  <div className="space-y-1.5 mt-2">
                    {mealsList.map((m, idx) => (
                      <div key={idx} className="text-xs bg-white p-2 rounded border flex justify-between">
                        <span><strong>{m.meal_type}:</strong> {m.items}</span>
                        <button onClick={() => setMealsList(mealsList.filter((_, i) => i !== idx))} className="text-red-600 font-bold">×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <button
            onClick={activeTab === 'workout' ? handleSaveWorkoutPlan : handleSaveDietPlan}
            className={`w-full py-3 rounded-xl text-white font-bold transition-all shadow-sm ${
              activeTab === 'workout' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            Save & Assign Plan
          </button>
        </div>

        {/* Existing Assigned Plans */}
        <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            Assigned {activeTab === 'workout' ? 'Workout Plans' : 'Diet Plans'}
          </h2>

          {activeTab === 'workout' ? (
            workoutPlans.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No workout plans assigned yet.</div>
            ) : (
              <div className="space-y-4 max-h-[500px] overflow-y-auto">
                {workoutPlans.map((plan) => (
                  <div key={plan.id} className="border border-gray-200 p-4 rounded-xl bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-bold text-gray-900">{plan.plan_name}</p>
                        <p className="text-xs text-orange-600 font-semibold">Member: {plan.member_name}</p>
                      </div>
                      <button onClick={() => handleDeleteWorkout(plan.id)} className="text-red-600 text-xs font-bold hover:underline">Delete</button>
                    </div>
                    <div className="space-y-1 text-xs text-gray-700 mt-2">
                      {plan.schedule?.map((s, i) => (
                        <div key={i} className="bg-white p-1.5 rounded border">
                          <strong>{s.day}:</strong> {s.exercise} ({s.sets_reps})
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            dietPlans.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No diet plans assigned yet.</div>
            ) : (
              <div className="space-y-4 max-h-[500px] overflow-y-auto">
                {dietPlans.map((plan) => (
                  <div key={plan.id} className="border border-gray-200 p-4 rounded-xl bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-bold text-gray-900">{plan.plan_name}</p>
                        <p className="text-xs text-green-600 font-semibold">Member: {plan.member_name}</p>
                      </div>
                      <button onClick={() => handleDeleteDiet(plan.id)} className="text-red-600 text-xs font-bold hover:underline">Delete</button>
                    </div>
                    <div className="flex gap-2 text-[11px] font-bold text-gray-600 bg-white p-2 rounded border mb-2">
                      <span>🔥 {plan.target_calories} kcal</span>
                      <span>🥩 P: {plan.protein_grams}g</span>
                      <span>🍞 C: {plan.carbs_grams}g</span>
                      <span>🥑 F: {plan.fats_grams}g</span>
                    </div>
                    <div className="space-y-1 text-xs text-gray-700">
                      {plan.meals?.map((m, i) => (
                        <div key={i} className="bg-white p-1.5 rounded border">
                          <strong>{m.meal_type}:</strong> {m.items}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
